import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import MailIcon from '@mui/icons-material/Mail';
import { useForm, Controller } from 'react-hook-form';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { Button, Modal, FormGroup, Label, ModalHeader, ModalBody, ModalFooter, Input } from 'reactstrap';
import { useEffect } from 'react';

import { useManageEmailNotifyModalStore } from './ManageEmailNotifyModalStore';

import usePutchEmailNotify from 'hooks/Object/usePutchEmailNotify';
import { EmailNotifySchema, EmailNotifyValues } from 'api/clients/types';

interface PutchEmailNotifyModalProps {
  refetchClients: () => void;
}

const PutchEmailNotifyModal: React.FC<PutchEmailNotifyModalProps> = ({ refetchClients }) => {
  const { putchEmailNotify } = usePutchEmailNotify();
  const {
    objectSlug,
    isOpen,
    onManageEmailNotifyModalClose,
    srbm_receive_messages,
    mct_receive_messages,
    smst_receive_messages,
    srst_receive_messages,
    mct_count,
    srbm_count,
    smst_count,
    srst_count,
  } = useManageEmailNotifyModalStore();
  const { t } = useTranslation();
  const { handleSubmit, control, reset } = useForm<EmailNotifyValues>({
    resolver: zodResolver(EmailNotifySchema),
    defaultValues: {
      mct_receive_messages: false,
      srbm_receive_messages: false,
      smst_receive_messages: false,
      srst_receive_messages: false,
    },
  });

  useEffect(() => {
    reset({
      mct_receive_messages: mct_receive_messages,
      srbm_receive_messages: srbm_receive_messages,
      smst_receive_messages: smst_receive_messages,
      srst_receive_messages: srst_receive_messages,
    });
  }, [mct_receive_messages, srbm_receive_messages, smst_receive_messages, srst_receive_messages, reset]);

  const onPutchEmailNotify = (data: EmailNotifyValues) => {
    putchEmailNotify(
      { objectSlug, notify: data },
      {
        onSuccess: () => {
          enqueueSnackbar(t('EmailNotifyModal.successUpdate'), {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          refetchClients();
          onManageEmailNotifyModalClose();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onManageEmailNotifyModalClose} size="md">
      <ModalHeader toggle={onManageEmailNotifyModalClose} />
      <ModalBody>
        <div className="d-flex align-items-center justify-content-center">
          <MailIcon color="action" fontSize="large" style={{ width: '100px', height: '100px' }} />
        </div>
        <h4 className="text-center">{t('EmailNotifyModal.title')}</h4>
        <p className="text-center my-2 text-muted">{t('EmailNotifyModal.subtitle')}</p>
        <div className="py-3 my-3 d-flex flex-column align-content-center align-items-start justify-content-center">
          <div className="mx-4 my-1">
            <FormGroup className="mb-1" check inline>
              <Controller
                control={control}
                name="mct_receive_messages"
                render={({ field }) => (
                  <>
                    <Input
                      type="checkbox"
                      id="mctReceiveMessagesCheckbox"
                      checked={field.value}
                      disabled={!(mct_count > 0)}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                    <Label check htmlFor="mctReceiveMessagesCheckbox">
                      {t('EmailNotifyModal.mctActivity')}
                    </Label>
                  </>
                )}
              />
            </FormGroup>
          </div>
          <div className="mx-4 my-1">
            <FormGroup className="mb-1" check inline>
              <Controller
                control={control}
                name="srbm_receive_messages"
                render={({ field }) => (
                  <>
                    <Input
                      type="checkbox"
                      id="srbmReceiveMessagesCheckbox"
                      disabled={!(srbm_count > 0)}
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                    <Label check htmlFor="srbmReceiveMessagesCheckbox">
                      {t('EmailNotifyModal.srbmActivity')}
                    </Label>
                  </>
                )}
              />
            </FormGroup>
          </div>
          <div className="mx-4 my-1">
            <FormGroup className="mb-1" check inline>
              <Controller
                control={control}
                name="smst_receive_messages"
                render={({ field }) => (
                  <>
                    <Input
                      type="checkbox"
                      id="smstReceiveMessagesCheckbox"
                      disabled={!(smst_count > 0)}
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                    <Label check htmlFor="smstReceiveMessagesCheckbox">
                      {t('EmailNotifyModal.smstActivity')}
                    </Label>
                  </>
                )}
              />
            </FormGroup>
          </div>
          <div className="mx-4 my-1">
            <FormGroup className="mb-1" check inline>
              <Controller
                control={control}
                name="srst_receive_messages"
                render={({ field }) => (
                  <>
                    <Input
                      type="checkbox"
                      id="srstReceiveMessagesCheckbox"
                      disabled={!(srst_count > 0)}
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                    <Label check htmlFor="srstReceiveMessagesCheckbox">
                      {t('EmailNotifyModal.srstActivity')}
                    </Label>
                  </>
                )}
              />
            </FormGroup>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn btn-ghost-danger"
          onClick={() => {
            onManageEmailNotifyModalClose();
          }}
        >
          {t('Modals.delete-modal.cancel')}
        </Button>{' '}
        <Button className="btn btn-ghost-success" onClick={handleSubmit(onPutchEmailNotify)}>
          {t('EmailNotifyModal.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PutchEmailNotifyModal;
